/**
 * service-registry.ts
 * Single source of truth for what the app offers. Each tile belongs to one
 * hub of the bottom tab bar (Campo, Yo); HubView.vue renders a hub's
 * tiles and has no per-service branching. Adding a new service means adding
 * one entry here with its hub.
 *
 * Visibility follows the user's capabilities (operator-tabs-by-role): a tile
 * shows only when the user holds the capability its screen's endpoint
 * requires, and a hub (tab) shows only when at least one of its available
 * tiles does, except Yo, which always shows because it holds the sign-out. The backend guards stay the authority; this only keeps the UI
 * from offering screens that would answer 403.
 *
 * 2026-10-31 (operator-home-hoy) — the Home launcher grid that mixed field
 * and HR tiles was replaced by the Hoy feed plus these hubs, so every tile
 * now declares the hub it lives in.
 *
 * operator-scan-tab (owner decision 2026-10-31): the QR scanner took the
 * Equipo tab's place in the bar ("no creo que la persona use para ver el
 * organigrama"); the org chart moved into Yo.
 */
import type { Component } from 'vue';
import { hasAnyCapability, hasCapability } from './capabilities.js';
import { Wrench, Route, Fuel, CalendarDays, CalendarClock, FileText, Network, ShieldCheck, Send, Package, Boxes } from '@lucide/vue';

export type HubSection = 'campo' | 'yo';

export interface ServiceTile {
  readonly id: string;
  readonly label: string;
  readonly icon: Component;
  readonly routeName: string | null;
  readonly status: 'available' | 'coming-soon';
  readonly section: HubSection;
  /**
   * Capability the screen's own read endpoint requires (its backend guard).
   * Null for coming-soon entries, which never decide a hub's visibility.
   */
  readonly requiredCapability: string | readonly string[] | null;
}

export interface Hub {
  readonly section: HubSection;
  readonly title: string;
  readonly routeName: string;
}

export const HUBS: readonly Hub[] = [
  { section: 'campo', title: 'Campo', routeName: 'hub-campo' },
  { section: 'yo', title: 'Mis cosas', routeName: 'hub-yo' },
] as const;

export const SERVICE_TILES: readonly ServiceTile[] = [
  { id: 'fallas', label: 'Fallas', icon: Wrench, routeName: 'fallas-list', status: 'available', section: 'campo', requiredCapability: 'locx.falla.leer' },
  { id: 'viajes', label: 'Gerenciamiento de viaje', icon: Route, routeName: 'trips-list', status: 'available', section: 'campo', requiredCapability: 'locx.viaje.leer' },
  { id: 'combustible', label: 'Carga de combustible', icon: Fuel, routeName: 'fuel-loads-list', status: 'available', section: 'campo', requiredCapability: 'locx.parte.leer' },
  // locx-voz-servicio-cross-tenant PR2 (D5) — the operadora's own read-only
  // history of servicio/servicio_futuro requests dispatched via "La Voz".
  { id: 'solicitudes-enviadas', label: 'Servicios Solicitados', icon: Send, routeName: 'solicitudes-enviadas', status: 'available', section: 'campo', requiredCapability: 'locx.servicio.crear' },
  // operator-mis-pedidos-repuestos — the repuesto intent's own read-only history.
  // odd/tasks/operator-panol.md — scan an article and act on it; the scan endpoint takes either stock read.
  { id: 'panol', label: 'Pañol', icon: Boxes, routeName: 'panol', status: 'available', section: 'campo', requiredCapability: ['locx.stock.leer', 'requi.stock.leer'] },
  { id: 'pedidos-repuestos', label: 'Mis Solicitudes', icon: Package, routeName: 'pedidos-repuestos', status: 'available', section: 'campo', requiredCapability: 'requi.solicitud.leer-propias' },
  // 'vacaciones' → 'ausencias' (nomi-autoservicio-empleado Tier A): the slot
  // is the general absence-request flow, not only vacation.
  { id: 'ausencias', label: 'Ausencias', icon: CalendarDays, routeName: 'ausencias-list', status: 'available', section: 'yo', requiredCapability: 'self.legajo.leer' },
  { id: 'calendario', label: 'Mi diagrama', icon: CalendarClock, routeName: 'mi-calendario', status: 'available', section: 'yo', requiredCapability: 'self.legajo.leer' },
  { id: 'documentos', label: 'Mis documentos', icon: FileText, routeName: 'mis-documentos', status: 'available', section: 'yo', requiredCapability: 'self.documento.leer' },
  // nomi-epp-firma T4 — the Campo "EPP" coming-soon slot became the employee's
  // own deliveries, signature and constancia; it lives next to Mis documentos.
  { id: 'epp', label: 'Mis EPP', icon: ShieldCheck, routeName: 'mis-epp', status: 'available', section: 'yo', requiredCapability: 'self.documento.leer' },
  { id: 'organigrama', label: 'Organigrama', icon: Network, routeName: 'organigrama', status: 'available', section: 'yo', requiredCapability: 'self.organigrama.leer' },
] as const;

/**
 * Voice intake accepts any intent the user may create (VOICE_INTAKE_INTENT_CAPABILITY
 * in packages/modules/servicios/src/http/voz/voice-intake.routes.ts).
 */
export const VOICE_INTAKE_CAPABILITIES: readonly string[] = ['locx.falla.crear', 'locx.servicio.crear'];

function canOpen(tile: ServiceTile, capabilities: readonly string[]): boolean {
  const required = tile.requiredCapability;
  if (required === null) return false;
  return typeof required === 'string' ? hasCapability(capabilities, required) : hasAnyCapability(capabilities, required);
}

/** The hub's tiles this user can open, plus its coming-soon entries when the hub has any. */
export function tilesFor(section: HubSection, capabilities: readonly string[]): readonly ServiceTile[] {
  const inHub = SERVICE_TILES.filter((tile) => tile.section === section);
  if (!inHub.some((tile) => canOpen(tile, capabilities))) return [];
  return inHub.filter((tile) => tile.status === 'coming-soon' || canOpen(tile, capabilities));
}

/**
 * A hub shows only when the user's capabilities open at least one of its
 * services. Mis cosas (section 'yo') used to stay visible for "Cerrar sesión";
 * that now lives in the profile sheet on Hoy (operator-hoy-perfil).
 */
export function isHubVisible(section: HubSection, capabilities: readonly string[]): boolean {
  return tilesFor(section, capabilities).length > 0;
}

export function visibleHubs(capabilities: readonly string[]): readonly Hub[] {
  return HUBS.filter((hub) => isHubVisible(hub.section, capabilities));
}

export function canUseVoice(capabilities: readonly string[]): boolean {
  return hasAnyCapability(capabilities, VOICE_INTAKE_CAPABILITIES);
}

/** The scanner tab (/escanear): same capability as GET /servicios/fleet-qr/:token. */
export const SCAN_CAPABILITY = 'locx.activo.leer';

export function canScan(capabilities: readonly string[]): boolean {
  return hasCapability(capabilities, SCAN_CAPABILITY);
}

export function hubFor(section: HubSection): Hub {
  const hub = HUBS.find((h) => h.section === section);
  if (!hub) throw new Error(`Unknown hub section: ${section}`);
  return hub;
}
